import { useMemo, useState } from 'react';
import {
  MFA_IDENTITY_TYPES,
  type MfaIdentity,
  type MFAIdentityType,
} from '@/entities/mfa-identity';
import type {
  ChangePasswordFormValues,
  MfaRequiredResponseData,
} from './types';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';

export type MfaView = 'list' | 'screen';

export const useMfaFlow = (form: UseFormReturn<ChangePasswordFormValues>) => {
  const { t } = useTranslation();

  const [view, setView] = useState<MfaView>('list');
  const [identityList, setIdentityList] = useState<MfaIdentity[] | null>(null);
  const [selectedIdentity, setSelectedIdentity] = useState<MfaIdentity | null>(
    null
  );

  const [maskedContact, setMaskedContact] = useState<string>('');

  const startMfaFlow = (mfaData: MfaRequiredResponseData) => {
    const { state, identityTypes } = mfaData;
    form.setValue('twoFA.state', state);

    const enabled = identityTypes.filter(i => i.isEnabled);

    if (enabled.length === 1) {
      const single = enabled[0];
      form.setValue('twoFA.identityType', single.name);
      setSelectedIdentity(single);
      setView('screen');
    } else {
      setIdentityList(enabled);
      setView('list');
    }
  };

  const selectIdentity = (type: MFAIdentityType) => {
    const identity = identityList?.find(i => i.name === type);
    if (!identity) return;
    form.setValue('twoFA.identityType', identity.name);
    setSelectedIdentity(identity);
    setView('screen');
  };

  const modalDescriptionText = useMemo(() => {
    if (selectedIdentity?.name === MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR) {
      return t('authPages.identityConfirmationForm.appDescription');
    }
    return maskedContact
      ? `${t('authPages.identityConfirmationForm.description')} ${maskedContact}`
      : '';
  }, [selectedIdentity?.name, t, maskedContact]);

  return {
    view,
    maskedContact,
    identityList,
    selectedIdentity,
    modalDescriptionText,
    startMfaFlow,
    selectIdentity,
    setMaskedContact,
    reset: () => {
      setView('list');
      setIdentityList(null);
      setSelectedIdentity(null);
    },
  };
};
